import { saveChartNumber, validateChartNumber } from '@/api/user';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { Icon, Input, Modal } from '@successmode-ktp/kui';
import ChartNumberDuplicateInfo from './ChartNumberDuplicateInfo';
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from 'react-query';
import styled from 'styled-components';
import { ValidateChartNumberTouristInfo } from '@/types/user';

type Props = {
  onClose: () => void;
  onSuccess?: () => void;
  selectedChartNumber?: string;
  selectedTouristIndex?: number;
  isEditVer?: boolean;
};

function ChartNumberModal({
  onClose,
  onSuccess,
  selectedChartNumber,
  selectedTouristIndex,
  isEditVer = false,
}: Props) {
  const { watch, setValue, setFocus } = useFormContext();
  const { isKioskStore } = useAppSelector((state) => state.user);
  const { touristIndex } = useAppSelector((state) => state.user);

  const [chartNumber, setChartNumber] = useState('');
  const [touristId, setTouristId] = useState(-1);
  const [error, setError] = useState('');
  const [isFocus, setIsFocus] = useState(true);
  const [duplicateInfos, setDuplicateInfos] = useState<
    ValidateChartNumberTouristInfo[]
  >([]);
  const [didCheck, setDidCheck] = useState(false);

  const { mutate: mutateSave } = useMutation(saveChartNumber);
  const { mutateAsync: mutateValidate } = useMutation(validateChartNumber);

  const onSave = async () => {
    if (isEdit && !hasChange) return;
    // 1) 저장 전 중복 검증
    if (!didCheck) {
      try {
        const res = await mutateValidate({ chartNumber });
        if (res?.isExist) {
          setDuplicateInfos(res.touristInfos || []);
          setDidCheck(true);
          setError('이미 사용 중인 고객이 있습니다.');
          return; // 중복이 존재하면 먼저 정보만 보여주고 저장은 보류
        }
      } catch (e) {
        alert(
          '차트번호 중복 사용이 확인되지 않았습니다. 잠시 후 다시 시도해주세요.',
        );
        return;
      }
    }

    // 2) 검증 통과(미존재) 또는 중복 확인 후 사용자 확인 단계에서 저장
    const payload = {
      touristId: touristId,
      chartNumber: chartNumber || null,
    };
    mutateSave(payload, {
      onSuccess: () => {
        setValue('chartNumber', chartNumber);
        onSuccess && onSuccess();
        handleClose();
      },
      onError: (error: any) => {
        const { code } = error.response?.data;
        if (code === 'T0001') {
          alert('고객 정보가 존재하지 않습니다.');
          return;
        }
        alert('차트번호 저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
      },
    });
  };

  const onChangeChartNumber = (e: ChangeEvent<HTMLInputElement>) => {
    const removedBlankValue = e.target.value.replaceAll(' ', '').trim();
    setChartNumber(removedBlankValue);
    setError('');
    setDidCheck(false);
    setDuplicateInfos([]);
  };

  const enteredChartNumber = watch('chartNumber');
  const isEdit = isEditVer || !!(enteredChartNumber || selectedChartNumber);

  // 차트번호 입력 시 공백 제거 후 비교
  const originalModified = useMemo(
    () =>
      (enteredChartNumber || selectedChartNumber || '')
        .replaceAll(' ', '')
        .trim(),
    [enteredChartNumber, selectedChartNumber],
  );

  // 차트번호 변경 여부 확인
  const hasChange = (chartNumber || '') !== originalModified;

  useEffect(() => {
    if (enteredChartNumber || selectedChartNumber) {
      setChartNumber(enteredChartNumber || selectedChartNumber);
    }
  }, [enteredChartNumber, selectedChartNumber]);

  useEffect(() => {
    if (selectedTouristIndex || touristIndex) {
      setTouristId(Number(selectedTouristIndex) || Number(touristIndex));
    }
  }, [touristIndex, selectedTouristIndex]);

  useEffect(() => {
    setIsFocus(true);
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Enter') {
      onSave();
    }
  };

  const handleClose = () => {
    onClose();
    if (isKioskStore) {
      setFocus('totalPrice');
    }
  };

  const ctaLabel = useMemo(() => {
    if (didCheck && duplicateInfos.length > 0) {
      return '같은 차트번호 사용하기';
    }
    if (isEdit) {
      return '변경하기';
    }
    return '추가하기';
  }, [didCheck, duplicateInfos.length, isEdit]);

  return (
    <BackDrop>
      <Popover>
        <Modal.Header>
          <Title>
            <Icon.Document size={32} />
            고객 차트번호 {isEdit ? '변경' : '입력'}
          </Title>
        </Modal.Header>
        <Content>
          <Description>
            병원에서 관리하는 고객의 차트번호를 입력해주세요.
          </Description>
          <Input
            id='chartNumber'
            value={chartNumber}
            onChange={onChangeChartNumber}
            placeholder='차트번호를 입력해주세요.'
            size='lg'
            error={error}
            autoFocus={isFocus}
            onKeyDown={onKeyDown}
          />
        </Content>
        {didCheck && duplicateInfos.length > 0 && (
          <ChartNumberDuplicateInfo
            infos={duplicateInfos}
            chartNumber={chartNumber}
          />
        )}
        <Modal.Footer>
          <Modal.Button variant='cancel' onClick={handleClose}>
            {isEdit ? '닫기' : '차트번호 없이 진행'}
          </Modal.Button>
          <Modal.Button
            variant='ok'
            onClick={onSave}
            disabled={isEdit ? !hasChange : !chartNumber}
          >
            {ctaLabel}
          </Modal.Button>
        </Modal.Footer>
      </Popover>
    </BackDrop>
  );
}

const BackDrop = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  min-width: 100vw;
  min-height: 100vh;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 40;
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  font-size: 21px;
  font-weight: 700;
  margin-bottom: 16px;
`;

const Content = styled.div`
  width: 100%;
`;

const Description = styled.p`
  color: #5f6165;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const Popover = styled.aside`
  position: fixed;
  background-color: var(--Gray-14);
  z-index: 50;
  padding: 32px;
  border-radius: 16px;
  max-height: '90vh';
  overflow-y: 'auto';
`;

export default ChartNumberModal;
